import { useEffect, useState, useRef } from "react";
import api, { API, apiErr, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, FileText, Download, Trash2, Loader2, Paperclip } from "lucide-react";

function fmtSize(b) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default function FileManager({ entityType, entityId, canDelete = true }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef();

  const load = async () => {
    const { data } = await api.get(`/files`, { params: { entity_type: entityType, entity_id: entityId } });
    setFiles(data);
  };
  useEffect(() => { load(); }, [entityId]);

  const onUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("entity_type", entityType);
    fd.append("entity_id", entityId);
    fd.append("file", file);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("File uploaded");
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); if (inputRef.current) inputRef.current.value = ""; }
  };

  const download = async (f) => {
    try {
      const res = await api.get(`/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const remove = async (f) => {
    await api.delete(`/files/${f.id}`);
    toast.success("File removed");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5" /> Attachments ({files.length})
        </h4>
        <input ref={inputRef} type="file" className="hidden" onChange={onUpload}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg" data-testid="file-input" />
        <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1.5" disabled={uploading}
          onClick={() => inputRef.current?.click()} data-testid="upload-file-button">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
        </Button>
      </div>
      {files.length === 0 ? (
        <p className="text-sm text-muted-foreground border border-dashed border-border rounded-sm p-4 text-center">
          No files yet. Upload Excel, Word, PDF or images.
        </p>
      ) : (
        <div className="space-y-2">
          {files.map((f) => (
            <div key={f.id} className="flex items-center gap-3 border border-border rounded-sm p-2.5 hover:bg-secondary/40 transition-colors" data-testid={`file-${f.id}`}>
              <div className="h-9 w-9 bg-primary/10 text-primary flex items-center justify-center rounded-sm shrink-0">
                <FileText className="h-4.5 w-4.5 h-[18px] w-[18px]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium truncate">{f.original_filename}</p>
                <p className="text-[11px] text-muted-foreground">{fmtSize(f.size)} · {f.uploader_name}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" onClick={() => download(f)} data-testid={`download-${f.id}`}>
                <Download className="h-4 w-4" />
              </Button>
              {canDelete && (
                <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-destructive hover:text-destructive" onClick={() => remove(f)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
